import { observer } from 'mobx-react-lite'
import {
  ReactElement,
  useMemo,
  useRef,
  useEffect,
  useState,
  MouseEvent,
} from 'react'
import { useUiStateStore } from '../../../../store/uiState/UiStateStore'
import { useSearchStore } from '../../../../store/search/SearchStore'
import { Button } from '../../../../ui-components/forms/Button'
import BaseDrawer from '../../../../ui-components/modals/BaseDrawer'
import styled from 'styled-components'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { defaultTheme } from '../../../../theme/index'
import { breakpoints } from '../../../../theme/breakpoints'
import { I18n, StorageKeys, Translate } from 'common'
import { useForm, FormProvider, useFieldArray, useWatch } from 'react-hook-form'
import CustomField from '../../../../ui-components/forms/customform/InputField'
import { Box, FormControlLabel, IconButton, useMediaQuery } from '@mui/material'
import { Delete } from '@mui/icons-material'
import { StoreInstances } from '../../../../store/StoreInstancesContainer'
import { MultiPartInquiry } from '../interfaces'
import { zIndex } from '../../../../theme/zIndex'
import { useNavigate } from 'react-router-dom'
import { createMultiPartInquiryBreadcrumb } from '../../../../helpers/utils'
import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import { IOSStyledSwitch } from '../../../../ui-components/util/IOSStyledSwitch'
import {
  getLineCodeForNewRow,
  getNextRowLineCodeUpdate,
} from './getPreviousLineCode'

const makeRow = (lineCode = ''): MultiPartInquiry => {
  return {
    lineCode,
    partNumber: '',
    stockQty: 1,
    comment: '',
  }
}

const asText = (value: unknown): string =>
  typeof value === 'string' ? value : ''

const getStoredCopyLineCodePreference = (): boolean => {
  return (
    localStorage.getItem(StorageKeys.COPY_LINE_CODE_FROM_PREVIOUS_ROW) ===
    'true'
  )
}

const validateLengthIntervalRequired = (
  value: string,
  rowHasEntries = false,
  min: number,
  max: number
): string | undefined => {
  if ((value === undefined || value === '') && rowHasEntries) {
    return 'Required'
  }
  if ((value?.length < min || value?.length > max) && rowHasEntries) {
    return `Min ${min} and max ${max} characters`
  }
  return undefined
}

const validateNumberIntervalRequired = (
  value: number,
  rowHasEntries = false,
  min: number,
  max: number
): string | undefined => {
  if ((value === undefined || value === null) && rowHasEntries) {
    return 'Required'
  }
  if ((value < min || value > max) && rowHasEntries) {
    return `Min ${min} and max ${max}`
  }
  return undefined
}

export const MultiPartInquiryDrawer = createNiceModal(
  observer((): ReactElement => {
    const modal = useModal()
    const navigate = useNavigate()
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobileLarge}px)`)
    const { searchMultiPartInquiry } = useSearchStore()
    const { setShowMultiPartInquiryImportDrawer } = useUiStateStore()
    const formRef = useRef<HTMLDivElement>(null)
    const [copyLineCodeEnabled, setCopyLineCodeEnabled] = useState(
      getStoredCopyLineCodePreference
    )

    const initialValues: Array<MultiPartInquiry> = useMemo(
      () => [...Array(5)].map(() => makeRow()),
      []
    )

    const methods = useForm({
      defaultValues: {
        items: initialValues,
      },
    })

    const { handleSubmit, control, reset, setValue, getValues } = methods

    const items = useWatch({
      control,
      name: 'items',
    })

    const { fields, remove, append } = useFieldArray({
      control,
      name: 'items',
    })

    const applyLineCodeUpdates = (
      updates: Array<{ index: number; lineCode: string }>
    ): void => {
      updates.forEach(({ index, lineCode }) => {
        setValue(`items.${index}.lineCode`, lineCode, {
          shouldDirty: true,
          shouldValidate: false,
        })
      })
    }

    const handleCopyLineCodeToggle = (
      event: MouseEvent<HTMLButtonElement>
    ): void => {
      event.stopPropagation()
      event.preventDefault()
      const nextValue = !copyLineCodeEnabled
      setCopyLineCodeEnabled(nextValue)
      localStorage.setItem(
        StorageKeys.COPY_LINE_CODE_FROM_PREVIOUS_ROW,
        String(nextValue)
      )
    }

    const handlePartNumberChange = (index: number, value: unknown): void => {
      if (!copyLineCodeEnabled) {
        return
      }
      const partNumber = asText(value)
      if (!partNumber.trim()) {
        return
      }
      const updatedItems = getValues('items').map((item, itemIndex) =>
        itemIndex === index ? { ...item, partNumber } : item
      )
      const nextRowUpdate = getNextRowLineCodeUpdate(updatedItems, index)
      if (nextRowUpdate) {
        applyLineCodeUpdates([nextRowUpdate])
      }
    }

    useEffect(() => {
      const lastItem = items?.at(-1)
      if (
        !!lastItem?.partNumber ||
        lastItem?.stockQty !== 1 ||
        !!lastItem?.comment
      ) {
        const lineCode = copyLineCodeEnabled ? getLineCodeForNewRow(items) : ''
        append(makeRow(lineCode), {
          shouldFocus: false,
        })
      }
    }, [append, items, copyLineCodeEnabled])

    const TableHeader = () => {
      return (
        <HeaderRow>
          <ColHeader width="15%">
            <I18n name="lineCode" />
          </ColHeader>
          <ColHeader width="34%">
            <I18n name="partNumberRequired" />
          </ColHeader>
          <ColHeader width="12%">
            <I18n name="qty" />
          </ColHeader>
          <ColHeader width="37%">
            <I18n name="comments" />
          </ColHeader>
          <ColHeader width="5%" />
        </HeaderRow>
      )
    }

    const onSubmit = (notTrimmedValues: { items: MultiPartInquiry[] }) => {
      const partsToSearch = notTrimmedValues.items
        .map((item) => {
          return {
            lineCode: item.lineCode?.trim().toUpperCase() || '',
            partNumber: item.partNumber?.trim() || '',
            stockQty: item.stockQty,
            comment: item.comment?.trim() || '',
          }
        })
        .filter((item) => item.partNumber !== '')

      if (partsToSearch?.length < 1) {
        StoreInstances.uiStore.displayErrorNotification(
          'addAtLeastOneItemToList'
        )
      } else {
        searchMultiPartInquiry(partsToSearch)
        StoreInstances.searchStore.handleAddBreadCrumb({
          page: 'multiPartInquiry',
          breadcumb: createMultiPartInquiryBreadcrumb(partsToSearch),
          url: () => searchMultiPartInquiry(partsToSearch),
        })
        reset({ items: initialValues })
        modal.remove()
        navigate('/searchResults')
      }
    }

    const drawerBody = (
      <BodyWrapper>
        <ToggleRow>
          <FormControlLabel
            control={
              <IOSStyledSwitch
                checked={copyLineCodeEnabled}
                onClick={handleCopyLineCodeToggle}
                inputProps={{
                  'aria-label': Translate('copyLineCodeFromPreviousRow'),
                }}
                size="small"
              />
            }
            label={Translate('copyLineCodeFromPreviousRow')}
          />
        </ToggleRow>
        <TableHeader />
        <Container ref={formRef}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box display="flex" flexDirection="column">
                <Content>
                  <Rows>
                    {fields.map((field, index) => {
                      const item = items[index]
                      const rowHasEntries =
                        !!item?.partNumber ||
                        item?.stockQty !== 1 ||
                        !!item?.comment

                      const validatePartNumber = (value: string) =>
                        validateLengthIntervalRequired(
                          value,
                          rowHasEntries,
                          1,
                          20
                        )

                      const validateStockQty = (value: string) => {
                        const numValue = Number(value)
                        return validateNumberIntervalRequired(
                          numValue,
                          rowHasEntries,
                          1,
                          999
                        )
                      }

                      return (
                        <InputWrapper key={field.id}>
                          <TextInputWrapper
                            width="15%"
                            $mobileWidth="72px"
                            $mobileGrow={false}
                          >
                            <MobileFieldLabel>
                              <I18n name="lineCode" />
                            </MobileFieldLabel>
                            <CustomField
                              name={`items.${index}.lineCode`}
                              type="text"
                              inputStyle={{ textTransform: 'uppercase' }}
                              wrapperStyle={{ margin: 0 }}
                              maxSize={3}
                            />
                          </TextInputWrapper>
                          <TextInputWrapper
                            width="34%"
                            $mobileWidth="0"
                            $mobileGrow
                          >
                            <MobileFieldLabel>
                              <I18n name="partNumberRequiredShort" />
                              <RequiredAsterisk>*</RequiredAsterisk>
                            </MobileFieldLabel>
                            <CustomField
                              name={`items.${index}.partNumber`}
                              validate={validatePartNumber}
                              type="text"
                              wrapperStyle={{ margin: 0 }}
                              onChange={(value) => {
                                handlePartNumberChange(index, value)
                              }}
                            />
                          </TextInputWrapper>
                          <TextInputWrapper
                            width="14%"
                            $mobileWidth="64px"
                            $mobileGrow={false}
                          >
                            <MobileFieldLabel>
                              <I18n name="qty" />
                            </MobileFieldLabel>
                            <CustomField
                              name={`items.${index}.stockQty`}
                              validate={validateStockQty}
                              type="number"
                              wrapperStyle={{ margin: 0 }}
                              maxSize={3}
                            />
                          </TextInputWrapper>
                          <TextInputWrapper
                            width="35%"
                            $hideOnMobile
                            $mobileWidth="100%"
                          >
                            <MobileFieldLabel>
                              <I18n name="comments" />
                            </MobileFieldLabel>
                            <CustomField
                              name={`items.${index}.comment`}
                              type="textarea"
                              wrapperStyle={{ margin: 0 }}
                              maxSize={45}
                            />
                          </TextInputWrapper>
                          <DeleteWrapper>
                            <IconButton
                              onClick={() => remove(index)}
                              aria-label={Translate('delete')}
                            >
                              <Delete />
                            </IconButton>
                          </DeleteWrapper>
                        </InputWrapper>
                      )
                    })}
                  </Rows>
                </Content>
                <ButtonsWrapper>
                  <Button
                    text="importFromFile"
                    onClick={() => {
                      modal.remove()
                      setShowMultiPartInquiryImportDrawer(true)
                    }}
                    variant="secondary"
                    icon={<StyledFileUploadIcon />}
                  />
                  <Button text="searchParts" variant="primary" type="submit" />
                </ButtonsWrapper>
              </Box>
            </form>
          </FormProvider>
        </Container>
      </BodyWrapper>
    )

    return (
      <BaseDrawer
        open={modal.visible}
        title="multiPartInquiry"
        body={drawerBody}
        onClose={() => {
          reset({ items: initialValues })
          modal.remove()
        }}
        width={isMobile ? `${window.innerWidth}px` : '900px'}
        minWidth={isMobile ? `${window.innerWidth}px` : '500px'}
        paddingX={isMobile ? '16px' : '2rem'}
        scrollable
      />
    )
  })
)

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0 8px;
  margin-bottom: 4px;
  max-width: 100%;
  min-width: 0;

  .MuiFormControlLabel-root {
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
  }

  .MuiSwitch-root {
    margin: 0 8px 0 0;
  }

  .MuiFormControlLabel-label {
    font-family: Roboto, serif;
    font-size: 14px;
    line-height: 20px;
    color: ${defaultTheme.colors.text};
    white-space: normal;
  }
`

const ButtonsWrapper = styled.div`
  justify-content: end;
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 8px;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-top: 8px;
    padding-bottom: 8px;

    button {
      width: 100%;
      min-width: 0;
    }
  }
`
const StyledFileUploadIcon = styled(FileUploadIcon)`
  margin: 0px 5px 2px 0px;
`

const HeaderRow = styled.div`
  background: ${defaultTheme.colors.rowFeaturedBackground};
  box-shadow:
    inset 0px -1px 0px ${defaultTheme.colors.borderTable},
    inset 0px 1px 0px ${defaultTheme.colors.borderTable};
  height: 40px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const ColHeader = styled.div<HeaderRowProps>`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  width: ${(p) => p.width};
  color: ${defaultTheme.colors.columnBorder};
  padding-right: 24px;
`
interface HeaderRowProps {
  width?: string
}

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`

interface TextInputWrapperProps {
  width: string
  $mobileWidth?: string
  $mobileGrow?: boolean
  $hideOnMobile?: boolean
}
const TextInputWrapper = styled.div<TextInputWrapperProps>`
  padding: 16px 16px 16px 0px;
  width: ${(p) => p.width};
  box-sizing: border-box;
  min-width: 0;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: ${(p) => (p.$hideOnMobile ? 'none' : 'flex')};
    flex-direction: column;
    width: ${(p) => (p.$mobileGrow ? 'auto' : (p.$mobileWidth ?? 'auto'))};
    flex: ${(p) => (p.$mobileGrow ? '1 1 0' : '0 0 auto')};
    padding: 0;
    gap: 4px;

    /* Hide character counters on mobile; keep validation errors */
    h6:not(.error) {
      display: none;
    }

    input,
    textarea {
      min-height: 40px;
      padding: 8px;
      width: 100%;
      box-sizing: border-box;
    }
  }
`

const MobileFieldLabel = styled.div`
  display: none;
  font-family: Roboto, serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: ${defaultTheme.colors.almostBlack};
  text-transform: none;
  margin-bottom: 0;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
    align-items: center;
    gap: 2px;
  }
`

const RequiredAsterisk = styled.span`
  color: ${defaultTheme.colors.red};
  font-weight: 700;
`

const DeleteWrapper = styled.div`
  width: 5%;
  padding: 16px 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: auto;
    padding: 0;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 2px;

    .MuiIconButton-root {
      padding: 8px;
    }
  }
`

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid ${defaultTheme.colors.borderTable};

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    flex-direction: row;
    align-items: flex-end;
    gap: 8px;
    padding: 12px 0;
  }
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
`

const Rows = styled.div`
  display: block;
  width: 100%;
`

const Container = styled.div`
  background-color: ${defaultTheme.colors.componentsBackground};
  z-index: ${zIndex.modal};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`
